import RemoteResourceRepository from './RemoteResourceRepository'
import {UniqueRecordError} from '../../utilities/errors'
import {isNil, keyBy, omitBy} from 'lodash'

/**
 * Private methods declarations
 */
const handleErrors = Symbol('handleErrors')
const mapQueriesToRemote = Symbol('mapQueriesToRemote')
const mapFiltersToRemote = Symbol('mapFiltersToRemote')

/*
 * RemoteNoteTypeRepository is a simple implementation of the RemoteRepository
*/
export default class RemoteNoteTypeRepository extends RemoteResourceRepository {
  constructor () {
    super('note-type')
  }

  /**
   * Maps note type filter parameters to match the ones used by the BriteNotes API
   * @param {Object} filters from the UI
   * @returns {Object} mapped filters
  */
  [mapFiltersToRemote] (filters) {
    const mappedFilters = omitBy(filters, (value, key) =>
      value === '' || isNil(value)
    )
    const jsonApiFilters = {}
    Object.keys(mappedFilters).map(key => {
      jsonApiFilters[`filter[${key}]`] = mappedFilters[key]
    })
    return jsonApiFilters
  }

  /**
   * Maps note type queries to match the ones used by the BriteNotes API
   * @param {Object} queries from the UI
   * @returns {Object} mapped queries
  */
  [mapQueriesToRemote] (queries) {
    if (queries) {
      if (queries.search) {
        if (!queries.filters) queries.filters = {}
        queries.filters.name = queries.search
      }
      if (queries.filters) {
        queries.filters = this[mapFiltersToRemote](queries.filters)
      }
      delete queries.search
    }
    return queries
  }

  [handleErrors] (e) {
    const {errors} = e
    if (errors) {
      const errorCodes = keyBy(errors, 'code')
      if (errorCodes.unique) {
        throw new UniqueRecordError(errorCodes.unique.detail)
      }
    }
    throw e
  }

  async listAllRecords ({queries = null}) {
    return super.listAllRecords({queries: this[mapQueriesToRemote](queries)})
  }

  async listRecords ({queries = null}) {
    return super.listRecords({queries: this[mapQueriesToRemote](queries)})
  }

  async createRecord (payload) {
    try {
      return await super.createRecord(payload)
    } catch (e) {
      return this[handleErrors](e)
    }
  }

  async updateRecord (payload) {
    try {
      return await super.updateRecord(payload)
    } catch (e) {
      return this[handleErrors](e)
    }
  }
}
